import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, AlertTriangle, Trash2, Lock } from 'lucide-react';
import { Button } from '../Button';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { readOnlyMessage } from '../../services/readOnlyNotice';
import {
  KATEGORIEN, MODULE, formatBytes, normalisiereOrdner, parseTags, pruefeDatei, titelAusDateiname, toggleModul,
} from './dokumenteLogic';
import { DateiIcon } from './DateiIcon';
import { DokumentEntwurf, DokumentKategorie, DokumentModul } from './types';

interface AusgewaehlteDatei {
  datei: File;
  titel: string;
  /** Absage aus `pruefeDatei` — die Zeile bleibt sichtbar, wird aber nicht hochgeladen. */
  absage: string | null;
}

interface Props {
  /** Ordner, die im Projekt schon benutzt werden — Vorschlagsliste. */
  ordnerVorschlaege: string[];
  /** Vorbelegte Kategorie (aus dem gerade offenen Reiter). */
  kategorieVorgabe?: DokumentKategorie;
  /** Vorbelegter Modulbezug — gesetzt, wenn aus einem Modul heraus hochgeladen wird. */
  modulVorgabe?: DokumentModul[];
  /**
   * Zweite Reihe gegen den Lesemodus. Der Dialog kennt sie selbst, weil
   * `isReadOnly` zur Laufzeit kippen kann (ein Admin uebernimmt die
   * Projektsperre) — ein bereits offener Dialog wuerde sonst weiterschreiben,
   * obwohl der Knopf, der ihn geoeffnet hat, laengst gesperrt ist.
   */
  isReadOnly?: boolean;
  /**
   * Darf das Admin-Schloss gesetzt werden (Admin oder Recht „„Nur
   * Admin“-Dokumente“)? Nur dann gibt es die Auswahl „nur für die Verwaltung".
   */
  darfSchloss?: boolean;
  onAbbrechen: () => void;
  /** Laedt EINE Datei hoch. Der Dialog ruft sie nacheinander je Datei auf. */
  onHochladen: (datei: File, entwurf: DokumentEntwurf) => Promise<void>;
}

/**
 * Der Weg, auf dem etwas in die Ablage kommt.
 *
 * Mehrere Dateien in einem Vorgang, aber NUR EINE Einordnung fuer alle: wer
 * fuenf Geruestfreigaben ablegt, ordnet sie fuenfmal gleich ein. Die
 * Feinheiten je Dokument (Stand, Ablaufdatum, Beschreibung) sind danach am
 * einzelnen Dokument zu setzen — sie einzeln schon hier abzufragen machte aus
 * einem Handgriff ein Formular je Datei.
 *
 * Der Titel ist die Ausnahme: er steht je Datei, weil er aus dem Dateinamen
 * vorbelegt wird und sich damit ohnehin unterscheidet.
 */
export const DokumentUploadDialog: React.FC<Props> = ({
  ordnerVorschlaege, kategorieVorgabe, modulVorgabe, isReadOnly = false, darfSchloss = false,
  onAbbrechen, onHochladen,
}) => {
  const [dateien, setDateien] = useState<AusgewaehlteDatei[]>([]);
  const [kategorie, setKategorie] = useState<DokumentKategorie>(kategorieVorgabe || 'sonstiges');
  const [ordner, setOrdner] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [module, setModule] = useState<DokumentModul[]>(modulVorgabe || []);
  const [nurAdmin, setNurAdmin] = useState(false);
  const [ziehtDarueber, setZiehtDarueber] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fortschritt, setFortschritt] = useState<{ fertig: number; gesamt: number } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const uebernehmen = (liste: FileList | null) => {
    if (!liste?.length) return;
    const neue: AusgewaehlteDatei[] = Array.from(liste).map(datei => ({
      datei,
      titel: titelAusDateiname(datei.name),
      absage: pruefeDatei({ name: datei.name, size: datei.size, type: datei.type }),
    }));
    setDateien(prev => [...prev, ...neue]);
    setFehler(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setZiehtDarueber(false);
    uebernehmen(e.dataTransfer.files);
  };

  const tauglich = dateien.filter(d => !d.absage);

  const starten = async () => {
    if (!tauglich.length || laeuft) return;
    if (isReadOnly) {
      setFehler(readOnlyMessage('Es wurde keine Datei abgelegt.'));
      return;
    }
    setLaeuft(true);
    setFehler(null);
    setFortschritt({ fertig: 0, gesamt: tauglich.length });
    const tags = parseTags(tagsText);
    const ordnerRein = normalisiereOrdner(ordner);
    try {
      // Nacheinander und nicht parallel: die Baustellenleitung ist schmal, und
      // fuenf gleichzeitige Uploads bringen dort nur alle fuenf zum Stocken.
      for (let i = 0; i < tauglich.length; i++) {
        const eintrag = tauglich[i];
        await onHochladen(eintrag.datei, {
          kategorie,
          ordner: ordnerRein,
          titel: eintrag.titel.trim() || titelAusDateiname(eintrag.datei.name),
          tags,
          module,
          nurAdmin,
        });
        // Die erledigte Datei faellt SOFORT aus der Auswahl. Bricht der naechste
        // Upload ab, zeigt der Dialog nur noch, was wirklich offen ist — sonst
        // laedt ein zweiter Anlauf die bereits abgelegten Dateien erneut hoch,
        // und die Ablage hat Dubletten samt doppeltem Speicher.
        setDateien(prev => prev.filter(x => x.datei !== eintrag.datei));
        setFortschritt({ fertig: i + 1, gesamt: tauglich.length });
      }
      onAbbrechen();
    } catch (e) {
      setFehler(rechtFehlerOder(e));
      setLaeuft(false);
      setFortschritt(null);
    }
  };

  const feld = 'w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm '
    + 'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
  const beschriftung = 'block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5';

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div>
            <h3 className="font-bold text-slate-800">Dokumente ablegen</h3>
            <p className="text-xs text-slate-500 mt-0.5">Mehrere Dateien auf einmal — sie erhalten dieselbe Einordnung.</p>
          </div>
          <button onClick={onAbbrechen} disabled={laeuft} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          {/* Ablagefläche */}
          <div
            data-tutorial="dokumente.ablagefeld"
            onDragOver={e => { e.preventDefault(); setZiehtDarueber(true); }}
            onDragLeave={() => setZiehtDarueber(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              ziehtDarueber ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-slate-100'
            }`}
          >
            <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 ${ziehtDarueber ? 'bg-blue-100' : 'bg-slate-100'}`}>
              <Upload className={`w-6 h-6 ${ziehtDarueber ? 'text-blue-600' : 'text-slate-400'}`} />
            </div>
            <p className="text-sm font-semibold text-slate-700">Dateien hierher ziehen</p>
            <p className="text-xs text-slate-500 mt-1">oder klicken zum Auswählen — PDF, Bilder, Office, CAD, ZIP (max. 50 MB je Datei)</p>
            <input
              ref={inputRef} type="file" multiple className="hidden"
              onChange={e => { uebernehmen(e.target.files); e.target.value = ''; }}
            />
          </div>

          {/* Ausgewählte Dateien */}
          {dateien.length > 0 && (
            <div className="space-y-2">
              {dateien.map((d, i) => (
                <div key={`${d.datei.name}-${i}`} className={`flex items-start gap-3 p-3 rounded-xl border ${d.absage ? 'border-rose-200 bg-rose-50/50' : 'border-slate-200 bg-white'}`}>
                  {/* Dieselbe Kennzeichnung wie spaeter in der Ablage — wer
                      hier PDF sieht, findet dort dasselbe Sinnbild wieder. Bei
                      einer Absage geht die Warnung vor: die Dateiart ist dann
                      belanglos, die Datei kommt gar nicht erst an. */}
                  {d.absage ? (
                    <div className="w-11 shrink-0 flex items-center justify-center py-1.5 rounded-lg border bg-rose-100 text-rose-600 border-rose-200">
                      <AlertTriangle className="w-4 h-4" />
                    </div>
                  ) : (
                    <DateiIcon dateiname={d.datei.name} mime={d.datei.type} />
                  )}
                  <div className="flex-1 min-w-0">
                    {d.absage ? (
                      <p className="text-xs text-rose-700 font-medium">{d.absage}</p>
                    ) : (
                      <input
                        value={d.titel}
                        onChange={e => setDateien(prev => prev.map((x, xi) => xi === i ? { ...x, titel: e.target.value } : x))}
                        disabled={laeuft}
                        className="w-full text-sm font-semibold text-slate-800 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-blue-500 outline-none"
                      />
                    )}
                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">{d.datei.name} · {formatBytes(d.datei.size)}</p>
                  </div>
                  <button
                    onClick={() => setDateien(prev => prev.filter((_, xi) => xi !== i))}
                    disabled={laeuft}
                    className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40 shrink-0"
                    title="Aus der Auswahl nehmen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Einordnung */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={beschriftung}>Kategorie</label>
              <select data-tutorial="dokumente.kategorie" value={kategorie} onChange={e => setKategorie(e.target.value as DokumentKategorie)} disabled={laeuft} className={feld}>
                {KATEGORIEN.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
              <p className="text-[11px] text-slate-400 mt-1">{KATEGORIEN.find(k => k.id === kategorie)?.hinweis}</p>
            </div>
            <div>
              <label className={beschriftung}>Ordner (optional)</label>
              <input
                value={ordner} onChange={e => setOrdner(e.target.value)} disabled={laeuft}
                list="dokumente-ordner" placeholder="z. B. Gerüst/BA 2" className={feld}
              />
              <datalist id="dokumente-ordner">
                {ordnerVorschlaege.map(o => <option key={o} value={o} />)}
              </datalist>
              <p className="text-[11px] text-slate-400 mt-1">Unterordner innerhalb der Kategorie. Leer lassen ist in Ordnung.</p>
            </div>
          </div>

          <div>
            <label className={beschriftung}>Schlagworte (optional)</label>
            <input
              value={tagsText} onChange={e => setTagsText(e.target.value)} disabled={laeuft}
              placeholder="mit Komma getrennt, z. B. Gerüst, Freigabe" className={feld}
            />
          </div>

          <div>
            <label className={beschriftung}>Auch anzeigen in</label>
            <p className="text-[11px] text-slate-400 mb-2">
              Die Ablage gehört nicht dem Koordinator allein. Was Sie hier ankreuzen, erscheint zusätzlich in der Dokumentenleiste des jeweiligen Moduls.
            </p>
            <div className="flex flex-wrap gap-2">
              {MODULE.map(m => {
                const an = module.includes(m.id);
                return (
                  <button
                    key={m.id} type="button" disabled={laeuft}
                    onClick={() => setModule(prev => toggleModul(prev, m.id))}
                    title={m.verfuegbar ? undefined : 'Das Modul ist noch in Entwicklung — die Zuordnung wird jetzt schon gespeichert.'}
                    className={`px-3 min-h-[44px] rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 ${
                      an ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {m.label}{m.verfuegbar ? '' : ' (bald)'}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Nur fuer Admins und Rechte-Inhaber. Die Auswahl auszublenden ist
              Bequemlichkeit — der Riegel ist die Policy: wer `nur_admin` ohne
              Recht trotzdem schickt, bekommt vom Server eine Absage. */}
          {darfSchloss && (
            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
              nurAdmin ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
            }`}>
              <input
                type="checkbox" checked={nurAdmin} disabled={laeuft}
                onChange={e => setNurAdmin(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-indigo-600"
              />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
                  <Lock className="w-3.5 h-3.5 text-indigo-600" /> Nur für die Verwaltung
                </span>
                <span className="block text-[11px] text-slate-500 mt-0.5">
                  Mit Admin-Schloss ablegen: Projektbeteiligte sehen diese Dateien nicht — weder in der Ablage
                  noch in den Modulen. Das Schloss lässt sich später jederzeit aufheben.
                </span>
              </span>
            </label>
          )}

          {fehler && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700 whitespace-pre-line">{fehler}</div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-3 shrink-0 bg-slate-50">
          <p className="text-xs text-slate-500">
            {fortschritt
              ? `${fortschritt.fertig} von ${fortschritt.gesamt} abgelegt…`
              : tauglich.length
                ? `${tauglich.length} ${tauglich.length === 1 ? 'Datei' : 'Dateien'} · ${formatBytes(tauglich.reduce((s, d) => s + d.datei.size, 0))}`
                : 'Noch keine Datei ausgewählt.'}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onAbbrechen} disabled={laeuft}>Abbrechen</Button>
            <Button
              data-tutorial="dokumente.hochladen"
              onClick={starten} disabled={!tauglich.length || laeuft || isReadOnly}
              icon={laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            >
              {laeuft ? 'Lädt hoch…' : 'Ablegen'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
